/**
 * Tab-wide registry of chats currently on screen.
 *
 * Multiple NovaProviders can co-mount in one tab (e.g. the global overlay
 * plus an embedded panel). "Is the user actively viewing this chat" must be
 * answered across all of them — the notification owner suppresses toasts
 * for a chat the user is reading in ANY provider, not just its own.
 */
const activeViews = new Map<symbol, string>();
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Register/replace the chat a provider currently shows (normalized uuid), or null when hidden. */
export function setActiveChatView(viewerKey: symbol, normalizedChatUuid: string | null): void {
  const previous = activeViews.get(viewerKey);
  if (normalizedChatUuid) {
    activeViews.set(viewerKey, normalizedChatUuid);
  } else {
    activeViews.delete(viewerKey);
  }

  if (previous !== (normalizedChatUuid ?? undefined)) {
    notifyListeners();
  }
}

export function clearActiveChatView(viewerKey: symbol): void {
  if (activeViews.delete(viewerKey)) {
    notifyListeners();
  }
}

export function isChatActivelyViewed(normalizedChatUuid: string): boolean {
  for (const viewed of activeViews.values()) {
    if (viewed === normalizedChatUuid) {
      return true;
    }
  }
  return false;
}

/**
 * Notifies when the set of on-screen chats changes, so the notification
 * owner can re-evaluate without waiting for a data change.
 */
export function subscribeActiveChatViews(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
